import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Scrollbars } from 'react-custom-scrollbars'

import { sendHoverSoundCommand, sendClickSoundCommand } from '../../helpers/web_to_client'

import styles from './ScrollArea.css'

const VERTICAL_SCROLL_WIDTH = 13
const HORIZONTAL_SCROLL_HEIGHT = 13
const HORIZONTAL_ARROW_INDENT = 13

const propTypes = {
    children: PropTypes.node.isRequired,
    scrollableAreaHeight: PropTypes.number.isRequired,

    type: PropTypes.oneOf(['vertical', 'horizontal']),
    scrollViewId: PropTypes.string,
    modifier: PropTypes.oneOf(['unitsList', 'table', 'postbattleDetails', 'toolbar', 'infotip', 'overflow']),
    scrollTopOffset: PropTypes.number,
    stepHeight: PropTypes.number,
    leftArrowMargin: PropTypes.number,
    rightArrowMargin: PropTypes.number,
    onScroll: PropTypes.func,
    onChangeScrollView: PropTypes.func,
    classNames: PropTypes.object,
    scrollBarsProps: PropTypes.object,
}

const defaultProps = {
    type: 'vertical',
    stepHeight: 10,
    leftArrowMargin: 0,
    rightArrowMargin: 0,
    classNames: {},
}

class ScrollArea extends React.PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            positionY: 0,
            positionX: 0,
            topArrowEnabled: false,
            bottomArrowEnabled: true,
            leftArrowEnabled: false,
            rightArrowEnabled: true,
        }
    }

    componentDidMount() {
        this._scrollbars.trackVertical.addEventListener('mousedown', this.handleTrackClick, true)
        this._scrollbars.trackHorizontal.addEventListener('mousedown', this.handleTrackClick, true)
        window.addEventListener('resize', this.handleWindowResize)

        if (this.props.scrollTopOffset && this.props.scrollTopOffset !== this.state.positionY) {
            this.scrollTop(this.props.scrollTopOffset)
        }
    }

    componentWillUnmount() {
        this._scrollbars.trackVertical.removeEventListener('mousedown', this.handleTrackClick, true)
        this._scrollbars.trackHorizontal.removeEventListener('mousedown', this.handleTrackClick, true)
        window.removeEventListener('resize', this.handleWindowResize)
    }

    componentWillReceiveProps(nextProps) {
        if (
            nextProps.scrollTopOffset !== undefined &&
            nextProps.scrollTopOffset !== null &&
            nextProps.scrollTopOffset !== this.state.positionY
        ) {
            this.scrollTop(nextProps.scrollTopOffset)
        }
    }

    handleTrackClick = (event) => {
        if (event.target === this._scrollbars.thumbVertical ||
            event.target === this._scrollbars.thumbHorizontal)
            sendClickSoundCommand()
    }

    handleWindowResize = () => {
        if (this.props.type === 'vertical') {
            this.scrollbarsUpdateVertical()
        } else {
            this.scrollbarsUpdateHorizontal()
        }
    }

    onChangeScrollView(isScrollEnabled) {
        if (this.props.onChangeScrollView) {
            this.props.onChangeScrollView(isScrollEnabled)
        }
    }

    scrollbarsUpdateVertical = (values) => {
        const scrollHeight = this._scrollbars.getScrollHeight()
        const clientHeight = this._scrollbars.getClientHeight()

        if (scrollHeight > clientHeight) {
            if (scrollHeight - clientHeight === 1) {
                this._scrollbars.thumbVertical.style.height = (this._scrollbars.trackVertical.offsetHeight - 1) + 'px'
                this._scrollbars.thumbVertical.style.transform = `translateY(${this._scrollbars.getScrollTop()}px)`
            }

            this._topArrow.classList.remove(styles.arrow__hidden)
            this._bottomArrow.classList.remove(styles.arrow__hidden)
            this.onChangeScrollView(true)
        } else {
            this._topArrow.classList.add(styles.arrow__hidden)
            this._bottomArrow.classList.add(styles.arrow__hidden)
            this.onChangeScrollView(false)
        }

        if (values && this.props.onScroll) {
            this.props.onScroll(values.scrollTop)
        }
    }

    scrollbarsUpdateHorizontal = (values) => {
        if (this._scrollbars.getScrollWidth() > this._scrollbars.getClientWidth()) {
            this._leftArrow.classList.remove(styles.arrow__hidden)
            this._rightArrow.classList.remove(styles.arrow__hidden)
            this.onChangeScrollView(true)
        } else {
            this._leftArrow.classList.add(styles.arrow__hidden)
            this._rightArrow.classList.add(styles.arrow__hidden)
            this.onChangeScrollView(false)
        }

        if (values && this.props.onScroll) {
            this.props.onScroll(values.scrollLeft)
        }
    }

    getClassNameWithProps(...names) {
        return classNames(names.flatMap((name) => [styles[name], this.props.classNames[name]]))
    }

    renderVerticalScrollThumb = (props) => {
        const customStyle = {
            width: VERTICAL_SCROLL_WIDTH,
        }

        return (
            <div
                {...props}
                style={customStyle}
                className={this.getClassNameWithProps('scrollThumbVertical')}
            >
                <div className={this.getClassNameWithProps('scrollThumbVerticalInner')} />
                <div className={this.getClassNameWithProps('scrollThumbCenter')} />
            </div>
        )
    }

    renderHorizontalScrollThumb = (props) => {
        const customStyle = {
            height: HORIZONTAL_SCROLL_HEIGHT,
        }

        return (
            <div
                {...props}
                style={customStyle}
                className={this.getClassNameWithProps('scrollThumbHorizontal')}
            >
                <div className={this.getClassNameWithProps('scrollThumbHorizontalInner')} />
                <div className={this.getClassNameWithProps('scrollThumbCenter')} />
            </div>
        )
    }

    renderVerticalScrollTrack = (props) => {
        const customStyle = {
            width: VERTICAL_SCROLL_WIDTH,
        }

        return (
            <div
                {...props}
                style={customStyle}
                className={this.getClassNameWithProps('scrollTrackVertical')}
                onMouseEnter={sendHoverSoundCommand}
            />
        )
    }

    renderHorizontalScrollTrack = (props) => {
        const customStyle = {
            height: HORIZONTAL_SCROLL_HEIGHT,
            right: HORIZONTAL_ARROW_INDENT + this.props.rightArrowMargin,
            left: HORIZONTAL_ARROW_INDENT + this.props.leftArrowMargin,
        }

        return (
            <div
                {...props}
                style={customStyle}
                className={this.getClassNameWithProps('scrollTrackHorizontal')}
                onMouseEnter={sendHoverSoundCommand}
            />
        )
    }

    renderView = ({ style, ...props}) => {
        const classNameScrollView = this.props.type === 'vertical' ?
            this.getClassNameWithProps('scrollViewVertical') :
            this.getClassNameWithProps('scrollViewHorizontal')

        if (style.marginBottom && !isNaN(style.marginBottom)) {
            // this is a fix for scroll bar being 1px visible on 2x scaling
            style.marginBottom = style.marginBottom - 1
        }

        return (
            <div
                id={this.props.scrollViewId}
                className={classNameScrollView}
                style={style}
                {...props}
            />
        )
    }

    scrollTop(top) {
        this.setState({
            positionY: top,
        })

        this._scrollbars.scrollTop(top)
    }

    scrollToTop() {
        this.setState({
            positionY: 0,
        })

        this._scrollbars.scrollToTop()
    }

    stepToTop(steps = 1) {
        const positionY = this.state.positionY > this.props.stepHeight ?
            this.state.positionY - this.props.stepHeight * steps : 0

        this.setState({
            positionY,
            topArrowEnabled: positionY > 0,
            bottomArrowEnabled: true,
        })

        this._scrollbars.scrollTop(positionY)
    }

    stepToBottom(steps = 1) {
        const maxHeight = this._scrollbars.getScrollHeight() - this.props.scrollableAreaHeight

        const positionY = this.state.positionY < maxHeight ?
            this.state.positionY + this.props.stepHeight * steps : maxHeight

        this.setState({
            positionY,
            topArrowEnabled: true,
            bottomArrowEnabled: positionY < maxHeight,
        })

        this._scrollbars.scrollTop(positionY)
    }

    stepToLeft(steps = 1) {
        const positionX = this.state.positionX > this.props.stepHeight ?
            this.state.positionX - this.props.stepHeight * steps : 0

        this.setState({
            positionX,
            leftArrowEnabled: positionX > 0,
            rightArrowEnabled: true,
        })

        this._scrollbars.scrollLeft(positionX)
    }

    stepToRight(steps = 1) {
        const maxWidth = this._scrollbars.getScrollWidth() - this._scrollbars.getClientWidth()
        const positionX = this.state.positionX < maxWidth ?
            this.state.positionX + this.props.stepHeight * steps : maxWidth

        this.setState({
            positionX,
            leftArrowEnabled: true,
            rightArrowEnabled: positionX < maxWidth,
        })

        this._scrollbars.scrollLeft(positionX)
    }

    handleTopArrowClick = () => {
        this.stepToTop()
    }

    handleBottomArrowClick = () => {
        this.stepToBottom()
    }

    handleLeftArrowClick = () => {
        this.stepToLeft()
    }

    handleRightArrowClick = () => {
        this.stepToRight()
    }

    handleMouseWheel = (e) => {
        const isVerticalScroll = this.props.type === 'vertical'

        if (e.deltaY < 0) {
            isVerticalScroll ? this.stepToTop() : this.stepToLeft()
        } else if (e.deltaY > 0) {
            isVerticalScroll ? this.stepToBottom() : this.stepToRight()
        }

        e.stopPropagation()

        if (!isVerticalScroll) {
            e.preventDefault()
        }
    }

    correctVerticalScrollPosition = () => {
        const maxHeight = this._scrollbars.getScrollHeight() - this.props.scrollableAreaHeight
        const scrollTop = this._scrollbars.getScrollTop()
        const difference = scrollTop - this.state.positionY
        let lastPosition

        if ((scrollTop % this.props.stepHeight) !== 0) {
            let coefficient
            if (difference > 0) {
                coefficient = Math.floor(scrollTop / this.props.stepHeight)
            } else {
                coefficient = Math.ceil(scrollTop / this.props.stepHeight)
            }
            lastPosition = coefficient * this.props.stepHeight
        } else {
            lastPosition = scrollTop
        }

        const isStateUpdateRequired = this.state.positionY !== lastPosition ||
            this.state.topArrowEnabled !== (scrollTop > 0) ||
            this.state.bottomArrowEnabled !== (scrollTop < maxHeight)
        if (isStateUpdateRequired) {
            this.setState({
                positionY: lastPosition,
                topArrowEnabled: scrollTop > 0,
                bottomArrowEnabled: scrollTop < maxHeight,
            })
        }
    }

    correctHorizontalScrollPosition = () => {
        const maxWidth = this._scrollbars.getScrollWidth() - this._scrollbars.getClientWidth()
        const scrollLeft = this._scrollbars.getScrollLeft()
        const difference = scrollLeft - this.state.positionX
        let lastPosition

        if ((scrollLeft % this.props.stepHeight) !== 0) {
            let coefficient
            if (difference > 0) {
                coefficient = Math.floor(scrollLeft / this.props.stepHeight)
            } else {
                coefficient = Math.ceil(scrollLeft / this.props.stepHeight)
            }
            lastPosition = coefficient * this.props.stepHeight
        } else {
            lastPosition = scrollLeft
        }

        const isStateUpdateRequired = this.state.positionX !== lastPosition ||
            this.state.leftArrowEnabled !== (scrollLeft > 0) ||
            this.state.rightArrowEnabled !== (scrollLeft < maxWidth)
        if (isStateUpdateRequired) {
            this.setState({
                positionX: lastPosition,
                leftArrowEnabled: scrollLeft > 0,
                rightArrowEnabled: scrollLeft < maxWidth,
            })
        }
    }

    getArrowClassName(position, enabled) {
        return classNames(
            styles.arrow,
            this.props.classNames.arrow,
            styles[`arrow__${position}`],
            this.props.classNames[`arrow__${position}`],
            !enabled && styles.arrow__disabled,
            !enabled && this.props.classNames.arrow__disabled,
        )
    }

    render() {
        const isVerticalScroll = this.props.type === 'vertical'

        const scrollBarsProps = {
            ref: (c) => this._scrollbars = c,
            hideTracksWhenNotNeeded: true,
            autoHeight: true,
            autoHeightMin: this.props.stepHeight,
            autoHeightMax: this.props.scrollableAreaHeight,
            renderView: this.renderView,
            onWheel: this.handleMouseWheel,
        }

        const noopDiv = () => <div />

        if (isVerticalScroll) {
            scrollBarsProps.onScrollFrame = this.correctVerticalScrollPosition
            scrollBarsProps.onUpdate = this.scrollbarsUpdateVertical
            scrollBarsProps.renderTrackVertical = this.renderVerticalScrollTrack
            scrollBarsProps.renderThumbVertical = this.renderVerticalScrollThumb
            scrollBarsProps.renderTrackHorizontal = noopDiv
            scrollBarsProps.renderThumbHorizontal = noopDiv
        } else {
            scrollBarsProps.onScrollFrame = this.correctHorizontalScrollPosition
            scrollBarsProps.onUpdate = this.scrollbarsUpdateHorizontal
            scrollBarsProps.renderTrackHorizontal = this.renderHorizontalScrollTrack
            scrollBarsProps.renderThumbHorizontal = this.renderHorizontalScrollThumb
            scrollBarsProps.renderTrackVertical = noopDiv
            scrollBarsProps.renderThumbVertical = noopDiv
        }

        if (this.props.scrollBarsProps) {
            Object.assign(scrollBarsProps, this.props.scrollBarsProps)
        }

        const classNameBase = classNames(
            styles.base,
            this.props.classNames.base,
            this.props.modifier && styles[`base__${this.props.modifier}`],
            this.props.modifier && this.props.classNames[`base__${this.props.modifier}`],
        )

        return (
            <div className={classNameBase}>
                {isVerticalScroll ? (
                    <div>
                        <div
                            ref={(c) => this._topArrow = c}
                            className={this.getArrowClassName('top', this.state.topArrowEnabled)}
                            onClick={this.handleTopArrowClick}
                            onMouseDown={sendClickSoundCommand}
                            onMouseEnter={sendHoverSoundCommand}
                        />

                        <div
                            ref={(c) => this._bottomArrow = c}
                            className={this.getArrowClassName('bottom', this.state.bottomArrowEnabled)}
                            onClick={this.handleBottomArrowClick}
                            onMouseDown={sendClickSoundCommand}
                            onMouseEnter={sendHoverSoundCommand}
                        />
                    </div>
                ) : (
                    <div>
                        <div
                            ref={(c) => this._leftArrow = c}
                            className={this.getArrowClassName('left', this.state.leftArrowEnabled)}
                            onClick={this.handleLeftArrowClick}
                            onMouseDown={sendClickSoundCommand}
                            onMouseEnter={sendHoverSoundCommand}
                            style={{left: this.props.leftArrowMargin}}
                        />

                        <div
                            ref={(c) => this._rightArrow = c}
                            className={this.getArrowClassName('right', this.state.rightArrowEnabled)}
                            onClick={this.handleRightArrowClick}
                            onMouseDown={sendClickSoundCommand}
                            onMouseEnter={sendHoverSoundCommand}
                            style={{right: this.props.rightArrowMargin}}
                        />
                    </div>
                )}

                <Scrollbars {...scrollBarsProps}>
                    {this.props.children}
                </Scrollbars>
            </div>
        )
    }
}

ScrollArea.propTypes = propTypes
ScrollArea.defaultProps = defaultProps

export default ScrollArea



// WEBPACK FOOTER //
// ../node_modules/react-mt-bootstrap/lib/components/ScrollArea/ScrollArea.jsx